// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { resolveRleventsAccess } from '@/lib/rleventsAccess';

interface PublicRouteProps {
  redirectTo?: string;
  children?: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { user, profile, isLoading } = useAuth();
  const location = useLocation();
  const [landingPath, setLandingPath] = useState<string | null>(null);
  const [accessLoading, setAccessLoading] = useState(true);

  const isPasswordResetPage =
    location.pathname === '/auth/reset-password' || location.pathname === '/reset-password';

  useEffect(() => {
    if (isPasswordResetPage) {
      setAccessLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (!user) {
        setAccessLoading(false);
        return;
      }
      try {
        const access = await resolveRleventsAccess(user.id);
        // Only redirect on a definite answer. Unknown leaves the page alone.
        if (!cancelled && access.status === 'ok') {
          // Hotel partners land on the portal unless the resolver already gave
          // them a real dashboard (admins/staff keep theirs).
          const path = access.roles?.includes('hotel_partner') && !access.isAdmin && !access.isStaff
            ? '/hotel-portal'
            : access.landingPath;
          setLandingPath(path);
        }
      } catch (err) {
        console.error('PublicRoute: access resolution failed', err);
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [user?.id, isPasswordResetPage]);

  // Password reset pages bypass ALL checks.
  if (isPasswordResetPage) {
    return children ? <>{children}</> : <Outlet />;
  }

  if (isLoading || accessLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg font-medium">Loading...</div>
      </div>
    );
  }

  if (user) {
    if (profile?.approval_status === 'pending') {
      return <Navigate to="/pending-approval" replace />;
    }
    if (landingPath) {
      return <Navigate to={landingPath} replace />;
    }
  }

  return children ? <>{children}</> : <Outlet />;
};

export default PublicRoute;
